import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  FormControlLabel,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AxiosConfig from '../../../../config/AxiosConfig';
import useDashboardFI from '../../../hooks/dashboard/useDashboardFI';
import { setSnackBar } from '../../../states/app/staticSlice.js';
import ExtractRefundButton from '../../utils/common/ExtractRefundButton';
import DashboardFiTable from './DashboardFiTable.jsx';

const CONTENT_TAB = {
  CURRENT: 'Current',
  HISTORY: 'History',
};
const currentRefundsStatus = [
  'TOVALIDATEFI',
  'WAITINGFI',
  'REFUNDFAILED',
  'REFUND_PENDING',
  'PARTIAL_REFUND_PENDING',
];
const historyRefundStatus = [
  'REFUSEDFI',
  'VALIDATEDFI',
  'REFUNDEDFI',
  'REFUND_FAILED_BO',
  'PARTIAL_REFUND_FAILED',
];

const sortOptions = [
  { label: 'Date ↑', field: 'createdAt', value: 1 },
  { label: 'Date ↓', field: 'createdAt', value: -1 },
];

const StatsBox = ({ type, date, count }) => {
  return (
    <Box
      sx={{
        width: 400,
        height: 100,
        border: '1px solid',
        borderColor: 'primary.main',
        borderRadius: '5px',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        padding: 2,
      }}
    >
      <Typography>
        {type} : {date}
      </Typography>
      <Typography sx={{ fontWeight: 'bold' }}>
        <Typography
          component="span"
          sx={{ color: 'primary.main', fontWeight: 'bold' }}
        >
          {count}
        </Typography>{' '}
        remboursements effectués
      </Typography>
    </Box>
  );
};

const FilterPaymentMethod = ({ items, checkedItems, updateFilter }) => {
  const handleChange = (id) => {
    updateFilter('selectedPaymentMethods', id); // Appelle updateFilter pour ajouter ou retirer l'élément
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ListSubheader component="div">Moyen de paiement</ListSubheader>
      <Box
        sx={{
          ml: 1, // Réduit l'indentation
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {items.map((item) => (
          <ListItem
            key={item}
            disablePadding // Supprime le padding par défaut
            sx={{
              py: 0.5, // Ajoute un léger padding vertical
            }}
          >
            <ListItemButton
              onClick={() => handleChange(item)}
              sx={{
                minHeight: 32, // Réduit la hauteur minimum
                padding: '4px 8px', // Compacte le padding intérieur
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: '32px', // Réduit la largeur de l'icône
                  margin: 0, // Supprime les marges
                  padding: 0, // Supprime les paddings
                }}
              >
                <FormControlLabel
                  key={item}
                  control={
                    <Checkbox
                      size="small" // Réduit la taille des cases à cocher
                      checked={checkedItems.includes(item)}
                    />
                  }
                />
              </ListItemIcon>
              <ListItemText
                primary={item}
                slotProps={{
                  primary: {
                    style: {
                      whiteSpace: 'nowrap', // Empêche les sauts de ligne
                      overflow: 'hidden', // Masque le texte débordant
                      textOverflow: 'ellipsis', // Ajoute "..." si le texte dépasse
                      fontSize: '0.875rem', // Réduit la taille de la police
                    },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </Box>
    </Box>
  );
};

const FilterSeller = ({ items, checkedItems, updateFilter }) => {
  const handleChange = (id) => {
    updateFilter('selectedSellers', id); // Appelle updateFilter pour ajouter ou retirer l'élément
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ListSubheader component="div">Vendeur</ListSubheader>
      <Box
        sx={{
          ml: 1, // Réduit l'indentation
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {items.map((item) => (
          <ListItem
            key={item._id}
            disablePadding // Supprime le padding par défaut
            sx={{
              py: 0.5, // Ajoute un léger padding vertical
            }}
          >
            <ListItemButton
              onClick={() => handleChange(item._id)}
              sx={{
                minHeight: 32, // Réduit la hauteur minimum
                padding: '4px 8px', // Compacte le padding intérieur
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: '32px', // Réduit la largeur de l'icône
                  margin: 0, // Supprime les marges
                  padding: 0, // Supprime les paddings
                }}
              >
                <FormControlLabel
                  key={item._id}
                  control={
                    <Checkbox
                      size="small" // Réduit la taille des cases à cocher
                      checked={checkedItems.includes(item._id)}
                    />
                  }
                />
              </ListItemIcon>
              <ListItemText
                primary={item.name}
                slotProps={{
                  primary: {
                    style: {
                      whiteSpace: 'nowrap', // Empêche les sauts de ligne
                      overflow: 'hidden', // Masque le texte débordant
                      textOverflow: 'ellipsis', // Ajoute "..." si le texte dépasse
                      fontSize: '0.875rem', // Réduit la taille de la police
                    },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </Box>
    </Box>
  );
};

const FilterStatus = ({ items, checkedItems, updateFilter }) => {
  const handleChange = (id) => {
    updateFilter('selectedStatuses', id); // Appelle updateFilter pour ajouter ou retirer l'élément
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ListSubheader component="div">Statuts</ListSubheader>
      <Box
        sx={{
          ml: 1, // Réduit l'indentation
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {items.map((item) => (
          <ListItem
            key={item._id}
            disablePadding // Supprime le padding par défaut
            sx={{
              py: 0.5, // Ajoute un léger padding vertical
            }}
          >
            <ListItemButton
              onClick={() => handleChange(item._id)}
              sx={{
                minHeight: 32, // Réduit la hauteur minimum
                padding: '4px 8px', // Compacte le padding intérieur
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: '32px', // Réduit la largeur de l'icône
                  margin: 0, // Supprime les marges
                  padding: 0, // Supprime les paddings
                }}
              >
                <FormControlLabel
                  key={item._id}
                  control={
                    <Checkbox
                      size="small" // Réduit la taille des cases à cocher
                      checked={checkedItems.includes(item._id)}
                    />
                  }
                />
              </ListItemIcon>
              <ListItemText
                primary={item.name}
                slotProps={{
                  primary: {
                    style: {
                      whiteSpace: 'nowrap', // Empêche les sauts de ligne
                      overflow: 'hidden', // Masque le texte débordant
                      textOverflow: 'ellipsis', // Ajoute "..." si le texte dépasse
                      fontSize: '0.875rem', // Réduit la taille de la police
                    },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </Box>
    </Box>
  );
};

const FilterType = ({ items, checkedItems, updateFilter }) => {
  const handleChange = (id, type) => {
    if (!id || !type) {
      console.warn('FilterType: handleChange called with invalid parameters');
      return;
    }

    switch (type) {
      case 'demandType': {
        const filteredItems = items.filter((item) => item.demandType === id);
        if (filteredItems.length === 0) {
          return;
        }

        const allChecked = filteredItems.every((item) =>
          checkedItems.includes(item._id)
        );

        filteredItems.forEach((item) => {
          const shouldBeChecked =
            !allChecked && !checkedItems.includes(item._id);
          if (allChecked || shouldBeChecked) {
            updateFilter('selectedTypes', item._id);
          }
        });
        break;
      }

      case 'subItem':
        updateFilter('selectedTypes', id);
        break;

      default:
        console.warn(`FilterType: Unsupported filter type: ${type}`);
        break;
    }
  };

  const demandTypes = useMemo(() => {
    return [...new Set(items.map((item) => item.demandType))];
  }, [items]);

  return (
    <Box
      sx={{
        marginBottom: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ListSubheader component="div">Types</ListSubheader>
      <Box
        sx={{
          ml: 0, // Réduit l'indentation
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {demandTypes.map((demandType) => {
          const filteredItems = items.filter(
            (item) => item.demandType === demandType
          );
          const isChecked = filteredItems.every((item) =>
            checkedItems.includes(item._id)
          );
          const isIndeterminate =
            filteredItems.some((item) => checkedItems.includes(item._id)) &&
            !isChecked;

          return (
            <Box
              key={demandType}
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ListItem
                disablePadding
                sx={{
                  py: 0.5, // Réduit le padding vertical
                }}
              >
                <ListItemButton
                  onClick={() => handleChange(demandType, 'demandType')}
                  sx={{
                    minHeight: 32,
                    padding: '4px 8px', // Compacte le padding intérieur
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 32,
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      indeterminate={isIndeterminate}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={demandType}
                    slotProps={{
                      primary: {
                        style: {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: '0.875rem',
                        },
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
              <Box
                sx={{
                  ml: 3, // Décale les sous-éléments
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {filteredItems.map((item) => (
                  <ListItem
                    key={item._id}
                    disablePadding
                    sx={{
                      py: 0.5, // Réduit le padding vertical
                    }}
                  >
                    <ListItemButton
                      onClick={() => handleChange(item._id, 'subItem')}
                      sx={{
                        minHeight: 28,
                        padding: '4px 8px', // Compacte le padding intérieur
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 32,
                          margin: 0,
                          padding: 0,
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={checkedItems.includes(item._id)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.name}
                        slotProps={{
                          primary: {
                            style: {
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              fontSize: '0.875rem',
                            },
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const FiltersDrawer = ({
  filters,
  updateFilter,
  paymentMethods,
  sellers,
  statuses,
  types,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box>
      {/* Bouton d'ouverture des filtres */}
      <Button
        variant="contained"
        color="primary"
        onClick={() => setIsOpen(true)}
        sx={{ marginBottom: 2 }}
      >
        Ouvrir les filtres
      </Button>

      {/* Drawer pour les filtres */}
      <Drawer
        sx={{
          width: 300,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
        anchor="right"
        open={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <Box
          sx={{
            width: 300,
            padding: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Conteneur scrollable pour les filtres */}
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
            {paymentMethods?.length > 1 && (
              <FilterPaymentMethod
                items={paymentMethods}
                checkedItems={filters.selectedPaymentMethods}
                updateFilter={updateFilter}
              />
            )}
            {sellers?.length > 1 && (
              <FilterSeller
                items={sellers}
                checkedItems={filters.selectedSellers}
                updateFilter={updateFilter}
              />
            )}
            <FilterStatus
              items={statuses}
              checkedItems={filters.selectedStatuses}
              updateFilter={updateFilter}
            />
            <FilterType
              items={types}
              checkedItems={filters.selectedTypes}
              updateFilter={updateFilter}
            />
          </Box>

          {/* Bouton Fermer en bas */}
          <Button
            onClick={() => setIsOpen(false)}
            sx={{
              marginTop: 'auto', // Cela pousse le bouton vers le bas
              width: '100%', // Prend toute la largeur du Drawer
              display: 'flex',
              justifyContent: 'center', // Centrer le texte
            }}
            variant="contained"
            color="info"
            startIcon={<CloseIcon />} // Ajouter une icône croix
          >
            Fermer
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

const DashboardFI = () => {
  const selfDate = new Date();
  const profile = useSelector((state) => state.login.profile);
  const statuses = useSelector((state) => state?.static?.refundStatus);
  const types = useSelector((state) => state?.static?.refundTypes);
  const [countersByPeriod, setCountersByPeriod] = useState({
    today: { count: 0, date: selfDate },
    pastWeek: { count: 0, date: selfDate },
    pastMonth: { count: 0, date: selfDate },
  });
  const dispatch = useDispatch();

  const [contentTab, setContentTab] = useState(CONTENT_TAB.CURRENT);
  const [orderRefs, setOrderRefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [limit, _setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(0);
  const [orderRef, setOrderRef] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [sellers, setSellers] = useState([]);

  const [filters, setFilters] = useState({
    selectedStatuses: [],
    selectedTypes: [],
    selectedPaymentMethods: [],
    selectedSellers: [],
  });

  const [dashboardData, setDashboardData] = useState({
    limit: limit,
    offset: offset,
    content: contentTab,
    orderRef: orderRef,
    filters: filters,
    orderBy: null,
    order: -1,
  });

  const { datas, res, isValidating, isLoading, mutate } = useDashboardFI(
    contentTab,
    dashboardData
  );

  const updateFilter = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: prevFilters[filterName].includes(value)
        ? prevFilters[filterName].filter((f) => f !== value)
        : [...prevFilters[filterName], value],
    }));
  };

  useEffect(() => {
    if (statuses) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        selectedStatuses: statuses
          .filter((item) => currentRefundsStatus.includes(item.code))
          .map((item) => item._id),
      }));
    }
  }, [statuses]);

  useEffect(() => {
    if (types) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        selectedTypes: types.map((item) => item._id),
      }));
    }
  }, [types]);

  useEffect(() => {
    if (paymentMethods) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        selectedPaymentMethods: paymentMethods,
      }));
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (sellers) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        selectedSellers: sellers.map((item) => item._id),
      }));
    }
  }, [sellers]);

  useEffect(() => {
    setDashboardData((state) => {
      return {
        ...state,
        offset: offset,
        content: contentTab,
        orderRef: orderRef,
        filters: filters,
      };
    });
  }, [contentTab, offset, orderRef, filters]);

  useEffect(() => {
    setOffset(page * limit);
  }, [page, limit]);

  useEffect(() => {
    setPage(0);
  }, [contentTab, orderRef, filters]);

  useEffect(() => {
    mutate();
    if (res && res?.status > 300) {
      const messageError = `Error: ${res?.status} - ${res?.statusText}: DashboardFI`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    } else {
      dispatch(setSnackBar({ open: false, message: null, color: null }));
    }
  }, [dashboardData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [
          countersResponse,
          ordersResponse,
          paymentMethodsReponse,
          sellersReponse,
        ] = await Promise.all([
          AxiosConfig.create('get', '/refunds/countersByPeriod')
            .call()
            .getResponseData(),
          AxiosConfig.create('get', '/refunds/orderReferences')
            .call()
            .getResponseData(),
          AxiosConfig.create('get', '/refunds/paymentMethods')
            .call()
            .getResponseData(),
          AxiosConfig.create('get', '/refunds/sellers')
            .call()
            .getResponseData(),
        ]);
        setCountersByPeriod(countersResponse);
        setOrderRefs(ordersResponse);
        setPaymentMethods(paymentMethodsReponse);
        setSellers(sellersReponse);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    setLoading(true);
    fetchInitialData();
  }, []);

  const handleChange = (event, newValue) => {
    setContentTab(newValue);
    switch (newValue) {
      case CONTENT_TAB.CURRENT:
        setFilters((prevFilters) => ({
          ...prevFilters,
          selectedStatuses:
            statuses
              ?.filter((item) => currentRefundsStatus.includes(item.code))
              ?.map((item) => item._id) || [],
        }));
        break;
      case CONTENT_TAB.HISTORY:
        setFilters((prevFilters) => ({
          ...prevFilters,
          selectedTypes: [],
          selectedPaymentMethods: [],
          selectedSellers: [],
          selectedStatuses:
            statuses
              ?.filter((item) => historyRefundStatus.includes(item.code))
              ?.map((item) => item._id) || [],
        }));
        break;
      default:
        break;
    }
  };

  const handleSearch = async (text) => {
    if (text && orderRefs.includes(text)) {
      setOrderRef(text);
    } else {
      setOrderRef(null);
    }
  };

  return (
    <>
      <Box sx={{ padding: 3, fontWeight: 'bold', fontSize: 24 }}>
        Bonjour {profile.firstName},
      </Box>
      <Box sx={{ display: 'flex', gap: 3, paddingLeft: 3 }}>
        <StatsBox
          type={"Aujourd'hui"}
          date={new Date(countersByPeriod.today.date).toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          })}
          count={countersByPeriod.today.count}
        />
        <StatsBox
          type={'Cette semaine'}
          date={`du ${new Date(countersByPeriod.pastWeek.date).toLocaleString(
            'fr-FR',
            {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }
          )} au ${new Date(countersByPeriod.today.date).toLocaleString(
            'fr-FR',
            {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }
          )}`}
          count={countersByPeriod.pastWeek.count}
        />
        <StatsBox
          type={'Depuis le'}
          date={new Date(countersByPeriod.pastMonth.date).toLocaleString(
            'fr-FR',
            {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
            }
          )}
          count={countersByPeriod.pastMonth.count}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 3, padding: 3 }}>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={contentTab} onChange={handleChange}>
              <Tab
                key={'Current'}
                label={`Demandes de remboursements ${
                  contentTab === 'Current' ? `(${datas?.totalCount})` : ''
                }`}
                value={'Current'}
              />
              <Tab
                key={'History'}
                label={`Historique des remboursements ${
                  contentTab === 'History' ? `(${datas?.totalCount})` : ''
                }`}
                value={'History'}
              />
            </Tabs>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ width: '50%', padding: 3, display: 'flex', gap: 1 }}>
                <Autocomplete
                  autoHighlight
                  disablePortal
                  options={orderRefs}
                  loading={loading}
                  getOptionLabel={(option) => option}
                  onChange={(e, newValue) => {
                    setSearchText(newValue);
                    handleSearch(newValue);
                  }}
                  sx={{ width: '100%' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Rechercher par numéro de commande"
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
                <Button
                  variant="contained"
                  onClick={() => handleSearch(searchText)}
                >
                  <SearchIcon />
                </Button>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box>
                <ExtractRefundButton />
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ display: 'flex', gap: 3, width: '20%' }}>
                <FiltersDrawer
                  filters={filters}
                  updateFilter={updateFilter}
                  statuses={statuses}
                  types={types}
                  paymentMethods={
                    contentTab === CONTENT_TAB.CURRENT ? paymentMethods : null
                  }
                  sellers={contentTab === CONTENT_TAB.CURRENT ? sellers : null}
                />
                <Autocomplete
                  disablePortal
                  options={sortOptions}
                  getOptionLabel={(option) => `Trier par: ${option.label}`}
                  onChange={(e, newValue) => {
                    setDashboardData((state) => {
                      return {
                        ...state,
                        orderBy: newValue ? newValue.field : null,
                        order: newValue ? newValue.value : null,
                      };
                    });
                  }}
                  sx={{ width: '50%' }}
                  renderInput={(params) => (
                    <TextField {...params} label="Trier par" />
                  )}
                />
              </Box>
            </Box>
          </Box>
          <DashboardFiTable
            datas={datas || []}
            profile={profile}
            page={page}
            setPage={setPage}
            limit={limit}
            isCurrentRefund={contentTab === CONTENT_TAB.CURRENT}
            loading={isLoading || isValidating}
          />
        </Box>
      </Box>
    </>
  );
};

export default DashboardFI;
